<template>
  <div class="atb-flex atb-flex-row atb-gap-4 atb-pt-6 atb-px-4 atb-pb-4">
    <div class="atb-flex-grow grid__height">
      <div class="eds-table-wrap atb-w-full atb-h-full">
        <AgGridVue
          id="historyTabGrid"
          class="ag-theme-eds atb-w-full atb-h-full"
          :columnDefs="columnDefs"
          :components="gridComponents"
          :rowModelType="'infinite'"
          :paginationPageSize="rowsPerPage"
          :pagination="true"
          :suppressPaginationPanel="true"
          :suppressCellSelection="true"
          :on-cell-clicked="versionNumberClickHandler"
          @grid-ready="onGridReady"
        />
        <Pagination
          v-model="currentPage"
          :page-count="totalPages"
          :total-rows="totalRows"
          :page-size="rowsPerPage"
          @pageClick="changePageAndRefresh"
          @pageSizeChange="changePageSizeAndRefresh"
          class="atb-bg-white"
        />
      </div>
    </div>
  </div>
  <Modal
    :show="displayVersionInfo"
    container-class="atb-w-1/2"
    body-class="atb-border-none"
    footer-class="atb-flex atb-flex-row atb-justify-end atb-gap-8"
    @onModalClose="displayVersionInfo = false"
  >
    <template #header>
      {{ codeType }} {{ tabName }}, {{ $t('version') }} {{ versionNumber }} for
      {{ currentDataSourceName }}
    </template>
    <template #body>
      <div class="atb-flex atb-flex-col atb-gap-4 atb-pb-2">
        {{ $t('currentVersion') }}: {{ $t('version') }}
        {{ currentVersionNumber }}
      </div>
      <div class="eds-table-wrap">
        <VAceEditor
          id="historyTabRollbackCodeEditor"
          :key="versionNumber ?? undefined"
          :value="currentVersionLuaCode"
          :lang="language"
          :theme="theme"
          :options="options"
          :readonly="editorReadOnly"
          :wrap="wordWrap"
          :print-margin="printMargin"
          :style="{ minHeight: '500px' }"
        ></VAceEditor>
      </div>
      <Modal
        id="historyTabRollbackConfirmationDialog"
        :show="displayConfirmationPopup"
        container-class="atb-w-96"
        body-class="atb-border-none"
        footer-class="atb-flex atb-flex-row atb-justify-end atb-gap-8"
        @onModalClose="displayConfirmationPopup = false"
      >
        <template #header> {{ $t('areYouSure') }}</template>
        <template #body>
          <div class="atb-flex atb-flex-col atb-gap-16">
            {{ $t('attributeRollbackCommit') }}
          </div>
        </template>
        <template #footer>
          <Button
            id="historyTabRollbackConfirmYesButton"
            motif="primary"
            :label="$t('yes')"
            :title="$t('yes')"
            @click="rollbackConfirmationHandler"
          />
          <Button
            id="historyTabRollbackConfirmNoButton"
            motif="primary"
            :label="$t('no')"
            :title="$t('no')"
            @click="displayConfirmationPopup = false"
          />
        </template>
      </Modal>
    </template>
    <template #footer>
      <Button
        id="historyTabCancelRollbackButton"
        motif="basic"
        :label="$t('cancel')"
        :title="$t('cancel')"
        @click="displayVersionInfo = false"
      />
      <Button
        id="historyTabRollbackButton"
        motif="primary"
        :label="$t('rollback')"
        :title="$t('rollback')"
        v-if="currentVersionNumber != versionNumber"
        @click="displayConfirmationPopup = true"
      />
    </template>
  </Modal>
</template>

<script lang="ts">
  export default { name: 'HistoryTab' };
</script>

<script lang="ts" setup>
  import { reactive, Ref, toRefs, ref, computed } from 'vue';
  import {
    AgGridEvent,
    CellClickedEvent,
    ColumnApi,
    GridApi,
  } from 'ag-grid-community';
  import Modal from '@atb-ui/eds-vue/src/components/Modal.vue';
  import Button from '@atb-ui/eds-vue/src/components/Button.vue';
  import Pagination from '@atb-ui/eds-vue/src/components/Pagination.vue';
  import { VAceEditor } from 'vue3-ace-editor';
  import { useI18n } from 'vue-i18n';
  import { AgGridVue } from 'ag-grid-vue3/lib/AgGridVue';
  import { usePagination } from '@/app/shared/hooks/use-pagination';
  import { GetHistoryDataEvent } from '@/app/shared/types/events/history-tab-event.interface';
  import {
    versionNumberRenderer,
    lastUpdatedRenderer,
    dataSourceNameRenderer,
  } from '@/app/shared/components/grid-cells/cellRenderers';
  import { PaginatedResponse } from '@/app/shared/types/api/api-response.interface';
  import { HistoryData } from '@/app/shared/types/history-data.interface';

  interface HistoryTabProps {
    clientId: number;
    serverSideDatasource: any;
    readOnly: boolean;
    codeMetaInformationsId: number;
    tabName: string;
    codeType: string;
    historyData: PaginatedResponse<HistoryData>;
  }

  interface HistoryTabEvent {
    (e: 'getHistoryTab', _params: GetHistoryDataEvent): void;
    (
      e: 'rollbackCode',
      rollbackRevisionId: number,
      chosenDataSourceName: string
    ): void;
  }
  const props = defineProps<HistoryTabProps>();
  const emit = defineEmits<HistoryTabEvent>();
  const { t } = useI18n();
  const {
    serverSideDatasource,
    codeType,
    tabName,
    historyData,
  } = toRefs(props);
  const gridApi = reactive({}) as Ref<GridApi | null>;
  const columnApi = reactive({}) as Ref<ColumnApi | null>;
  const {
    currentPage,
    rowsPerPage,
    totalRows,
    totalPages,
    changePage,
    changePageSize,
    calculatePagination,
  } = usePagination(
    historyData.value.numberOfElements,
    historyData.value.totalElements,
    gridApi
  );

  const changePageAndRefresh = (page: number) => {
    changePage(page);
    gridApi.value?.setDatasource(
      serverSideDatasource.value(
        currentPage.value,
        rowsPerPage.value,
        calculatePagination
      )
    );
  };

  const changePageSizeAndRefresh = (newPageSize: number | string) => {
    changePageSize(newPageSize);
    gridApi.value?.setDatasource(
      serverSideDatasource.value(
        currentPage.value,
        rowsPerPage.value,
        calculatePagination
      )
    );
  };

  const columnDefs = [
    {
      field: 'versionNumber',
      headerName: t('version'),
      suppressMovable: true,
      width: 150,
      sortable: true,
      sort: 'desc',
      suppressSizeToFit: true,
      cellRenderer: 'versionNumberRenderer',
    },
    {
      field: 'dataSourceName',
      headerName: t('dataSourceHistory'),
      suppressMovable: true,
      sortable: true,
      suppressSizeToFit: true,
      cellRenderer: 'dataSourceNameRenderer',
      hide: codeType.value === 'Attribute',
    },
    {
      field: 'action',
      headerName: t('action'),
      suppressMovable: true,
      sortable: true,
      suppressSizeToFit: true,
    },
    {
      field: 'createdAt',
      headerName: t('date'),
      suppressMovable: true,
      sortable: true,
      suppressSizeToFit: true,
      cellRenderer: 'lastUpdatedRenderer',
    },
    {
      field: 'createdBy',
      headerName: t('editedBy'),
      suppressMovable: true,
      sortable: true,
      suppressSizeToFit: true,
    },
    {
      field: 'comments',
      headerName: t('comments'),
      suppressMovable: true,
      sortable: true,
      suppressSizeToFit: false,
    },
  ];

  const gridComponents = {
    versionNumberRenderer,
    lastUpdatedRenderer,
    dataSourceNameRenderer,
  };

  const onGridReady = (params: AgGridEvent) => {
    gridApi.value = params.api;
    columnApi.value = params.columnApi;
    gridApi.value?.setDatasource(
      serverSideDatasource.value(
        currentPage.value,
        rowsPerPage.value,
        calculatePagination
      )
    );
    gridApi.value.sizeColumnsToFit();
  };

  const rollbackConfirmationHandler = async () => {
    displayConfirmationPopup.value = false;
    displayVersionInfo.value = false;
    emit('rollbackCode', rollbackRevisionId.value, chosenDataSourceName.value);
  };

  const displayVersionInfo = ref(false);
  const displayConfirmationPopup = ref(false);

  const versionNumber = ref<number | null>(null);
  const currentVersionNumber = ref<number | null>(null);
  const rollbackRevisionId = ref();

  const currentDataSourceName = computed(() => {
    return chosenDataSourceName.value;
  });

  const currentVersionLuaCode = ref('');
  const chosenDataSourceName = ref('');

  const editorReadOnly = ref(true);
  const language = ref('lua');
  const theme = ref('chrome');
  const options = reactive({
    fontFamily: 'monospace',
    highlightActiveLine: true,
    highlightGutterLine: false,
  });
  const printMargin = ref(false);
  const wordWrap = ref(true);

  const versionNumberClickHandler = (event: CellClickedEvent) => {
    const version = Number(event.value);
    if (
      event.colDef.field === 'versionNumber' &&
      Number.isFinite(version) &&
      version !== 0
    ) {
      versionNumber.value = Number(event.data.versionNumber);
      currentVersionNumber.value = Number(event.data.currentVersion);
      displayVersionInfo.value = true;
      chosenDataSourceName.value = event.data.dataSourceName;
      currentVersionLuaCode.value = event.data.code ? String(event.data.code) : '';

      rollbackRevisionId.value = event.data.revisionId;

      displayVersionInfo.value = true;
    }
  };

  defineExpose({
    gridApi,
    changePageAndRefresh,
  });
</script>
<style scoped>
  .grid__height {
    min-height: calc(100vh - (245px));
  }
</style>
